type NumericLike = number | string | null | undefined;

type ReceiptLike = {
  enteredQuantity?: NumericLike;
  enteredUnitLabel?: string | null;
  quantity?: NumericLike;
  baseQuantityReceived?: NumericLike;
  baseUnitLabel?: string | null;
};

type MovementLike = {
  enteredQuantity?: NumericLike;
  enteredUnitLabel?: string | null;
  quantity?: NumericLike;
  quantityDelta?: NumericLike;
  baseQuantityDelta?: NumericLike;
  unit?: string | null;
  baseUnitLabel?: string | null;
};

export function formatInventoryQuantity(value: NumericLike) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return '-';
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export function formatInventoryMeasurement(value: NumericLike, unit?: string | null) {
  const amount = formatInventoryQuantity(value);
  return unit ? `${amount} ${unit}` : amount;
}

export function formatSignedInventoryMeasurement(value: NumericLike, unit?: string | null) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return formatInventoryMeasurement(value, unit);
  const prefix = numeric > 0 ? '+' : numeric < 0 ? '-' : '';
  return `${prefix}${formatInventoryMeasurement(Math.abs(numeric), unit)}`;
}

export function receiptShowsBaseQuantity(receipt: ReceiptLike, fallbackBaseUnit?: string | null) {
  const enteredUnit = String(receipt.enteredUnitLabel || fallbackBaseUnit || '').trim().toLowerCase();
  const baseUnit = String(receipt.baseUnitLabel || fallbackBaseUnit || '').trim().toLowerCase();
  const enteredQuantity = Number(receipt.enteredQuantity ?? receipt.quantity ?? 0);
  const baseQuantity = Number(receipt.baseQuantityReceived ?? receipt.quantity ?? 0);
  return enteredUnit !== baseUnit || Math.abs(enteredQuantity - baseQuantity) > 0.000001;
}

export function formatInventoryReceiptSummary(receipt: ReceiptLike, fallbackBaseUnit?: string | null) {
  const receiptUnit = receipt.enteredUnitLabel || fallbackBaseUnit || '';
  const baseUnit = receipt.baseUnitLabel || fallbackBaseUnit || '';
  const primary = formatInventoryMeasurement(receipt.enteredQuantity ?? receipt.quantity, receiptUnit);
  const secondary = receiptShowsBaseQuantity(receipt, fallbackBaseUnit)
    ? `Base UOM: ${formatInventoryMeasurement(receipt.baseQuantityReceived ?? receipt.quantity, baseUnit)}`
    : null;
  return { primary, secondary };
}

export function movementBaseDelta(movement: MovementLike) {
  return Number(movement.baseQuantityDelta ?? movement.quantityDelta ?? movement.quantity ?? 0);
}

export function movementEnteredDelta(movement: MovementLike) {
  const baseDelta = movementBaseDelta(movement);
  const enteredQuantity = Number(movement.enteredQuantity ?? movement.quantity ?? 0);
  if (baseDelta < 0) return -Math.abs(enteredQuantity);
  if (baseDelta > 0) return Math.abs(enteredQuantity);
  return 0;
}

export function formatInventoryMovementDeltaSummary(movement: MovementLike) {
  const primary = formatSignedInventoryMeasurement(
    movementEnteredDelta(movement),
    movement.enteredUnitLabel || movement.unit,
  );
  const secondary = `Base UOM: ${formatSignedInventoryMeasurement(
    movementBaseDelta(movement),
    movement.baseUnitLabel || movement.unit,
  )}`;
  return { primary, secondary };
}
